import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const RESULT_SCROLL_HINT_HIDE_RATIO = 0.15;
const SIDE_LOOP_BASE_DURATION = 20;
const SIDE_LOOP_BASE_DISTANCE = 1262 + 164;
const SIDE_IMAGE_BASE_WIDTH = 61;
// _top.scssの抽選待機演出と同期し、完了後に既存の結果演出へ切り替える。
const DRAWING_DURATION = 2600;

const RESULTS_URL = "/_inc/special/yomikuji/data/results.json";

export const selectRandomResult = (results, currentId = null, random = Math.random) => {
    const candidates = results.filter((result) => result.id !== currentId);

    if (!candidates.length) return null;

    const index = Math.min(Math.floor(random() * candidates.length), candidates.length - 1);
    return candidates[index];
};

class Yomikuji {
    constructor() {
        this.top = document.querySelector(".js-yomikuji-top");
        this.result = document.querySelector(".js-yomikuji-result");
        this.resultContent = document.querySelector(".js-yomikuji-result-content");
        this.mount = document.querySelector(".js-yomikuji-result-mount");
        this.drawButton = document.querySelector(".js-yomikuji-draw");
        this.boardAura = document.querySelector(".js-yomikuji-aura");
        this.shareButton = document.querySelector(".js-yomikuji-share");
        this.resultScrollHint = document.querySelector(".js-yomikuji-result-scroll-hint");
        this.template = document.querySelector(".js-yomikuji-result-template");
        this.resultRevealElements = [...document.querySelectorAll(".js-yomikuji-result .js-scrollreveal")];
        this.results = null;
        this.resultsPromise = null;
        this.currentId = null;
        this.currentTitle = null;
        this.isDrawing = false;
        this.isPreparing = false;
        this.resultScrollHintObserver = null;
        this.resultScrollHintUpdateFrame = null;
        this.currentShareId = null;

        this.init();
    }

    init() {
        if (!this.top || !this.result || !this.resultContent || !this.mount || !this.drawButton || !this.boardAura || !this.shareButton || !this.template) return;

        this.disableResultReveal();
        this.initResultScrollHint();

        this.drawButton.addEventListener("click", () => this.draw());
        this.boardAura.addEventListener("animationend", (event) => {
            if (event.animationName !== "yomikujiBoardAuraPulse") return;
            document.body.classList.remove("-yomikuji-aura-active");
        });
        this.shareButton.addEventListener("click", () => this.shareResult());
        this.result.addEventListener("click", (event) => {
            if (!event.target.closest(".js-yomikuji-return-top")) return;
            this.showTop();
        });

        // 「ひいてみる」押下時にネットワーク待ちを挟まないよう、結果JSONは初期化時から先読みしておく。
        this.loadResults();
        this.initDebugResult();
    }

    loadResults() {
        if (this.resultsPromise) return this.resultsPromise;

        const url = this.result.dataset.resultsUrl || RESULTS_URL;

        this.resultsPromise = fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error(`results.json responded ${response.status}`);
                return response.json();
            })
            .then((results) => {
                this.results = Array.isArray(results) ? results : [];
                return this.results;
            })
            .catch(() => {
                // 通信失敗時は次の操作で取り直せるよう、保持したPromiseを捨ててエラーを外へ出さない。
                this.resultsPromise = null;
                return null;
            });

        return this.resultsPromise;
    }

    async initDebugResult() {
        const debugToken = this.getDebugResultToken();
        if (!debugToken) return;

        const results = await this.loadResults();
        const debugResult = results?.find((result) => result.debugToken === debugToken) ?? null;
        if (!debugResult) return;

        const showDebugResult = () => this.showResult(debugResult, { skipAnimations: true });

        if (document.readyState === "complete") {
            showDebugResult();
        } else {
            window.addEventListener("load", showDebugResult, { once: true });
        }
    }

    getDebugResultToken() {
        const debugToken = new URLSearchParams(window.location.search).get("debugResult");
        if (!/^\d{5}$/.test(debugToken ?? "")) return null;

        return debugToken;
    }

    finishDebugAnimations() {
        if (typeof this.result.getAnimations !== "function") return;

        // デバッグ直表示では、無限ループ以外の演出を完了状態まで進めて結果確認を待たせない。
        this.result.getAnimations({ subtree: true }).forEach((animation) => {
            const endTime = animation.effect?.getComputedTiming().endTime;
            if (!Number.isFinite(endTime)) return;

            try {
                animation.finish();
            } catch (error) {
                // finishできないAnimationは通常再生へフォールバックする。
            }
        });
    }

    getResultRevealTriggers() {
        return ScrollTrigger.getAll().filter((trigger) => this.resultRevealElements.includes(trigger.trigger));
    }

    disableResultReveal() {
        this.getResultRevealTriggers().forEach((trigger) => trigger.disable());
        this.resultRevealElements.forEach((element) => element.classList.remove("-reveal"));
    }

    enableResultReveal() {
        this.getResultRevealTriggers().forEach((trigger) => {
            trigger.enable(true, true);
            trigger.update();
        });
    }

    setSideAnimationDuration() {
        const sideImage = this.mount.querySelector(".yomikuji-result__side-track img");
        const fortune = this.mount.querySelector(".yomikuji-result__fortune");
        if (!sideImage || !fortune) return;

        const updateDuration = () => {
            const imageRect = sideImage.getBoundingClientRect();
            const imageMargin = Number.parseFloat(getComputedStyle(sideImage).marginBottom);
            if (!imageRect.width || !imageRect.height || !Number.isFinite(imageMargin)) return;

            // SVGの文字サイズを揃えたまま、素材の長さが変わっても従来と同じ移動速度を保つ。
            const loopDistance = imageRect.height + imageMargin;
            const baseDistance = (imageRect.width / SIDE_IMAGE_BASE_WIDTH) * SIDE_LOOP_BASE_DISTANCE;
            const duration = SIDE_LOOP_BASE_DURATION * (loopDistance / baseDistance);

            fortune.style.setProperty("--ymkj-side-duration", `${duration}s`);
        };

        if (sideImage.complete) {
            updateDuration();
        } else {
            sideImage.addEventListener("load", updateDuration, { once: true });
        }
    }

    initResultScrollHint() {
        if (!this.resultScrollHint) return;

        const requestUpdate = () => {
            if (this.resultScrollHintUpdateFrame !== null) return;

            this.resultScrollHintUpdateFrame = requestAnimationFrame(() => {
                this.resultScrollHintUpdateFrame = null;
                this.updateResultScrollHint();
            });
        };

        if ("IntersectionObserver" in window) {
            this.resultScrollHintObserver = new IntersectionObserver(requestUpdate);
            this.resultScrollHintObserver.observe(this.shareButton);
        }

        // Endキーなどで監視対象を飛び越えた場合も、矢印の表示状態を取り残さない。
        window.addEventListener("scroll", requestUpdate, { passive: true });
        window.addEventListener("resize", requestUpdate);

        this.resultScrollHint.addEventListener("transitionend", (event) => {
            if (event.propertyName !== "opacity" || !this.resultScrollHint.classList.contains("-show")) return;
            this.resultScrollHint.classList.add("-shown");
        });
    }

    updateResultScrollHint() {
        if (!this.resultScrollHint) return;

        const shareButtonRect = this.shareButton.getBoundingClientRect();
        const hideThreshold = shareButtonRect.height * RESULT_SCROLL_HINT_HIDE_RATIO;
        const isResultVisible = document.body.classList.contains("-yomikuji-result");
        const isBeforeHideThreshold = shareButtonRect.top > window.innerHeight - hideThreshold;
        this.resultScrollHint.classList.toggle("-show", isResultVisible && isBeforeHideThreshold);
    }

    shareResult() {
        const shareId = this.currentShareId;

        if (!this.currentTitle || !/^\d{5}$/.test(shareId ?? "")) return;

        const shareText = `今日の #黄泉くじ は…\n【${this.currentTitle}】でした！\n#黄泉のツガイ #ヨミツガ`;
        const shareUrl = new URL("https://yominotsugai.com/special/yomikuji/");
        const intentUrl = new URL("https://x.com/intent/tweet");

        shareUrl.searchParams.set("id", shareId);
        intentUrl.searchParams.set("text", shareText);
        intentUrl.searchParams.set("url", shareUrl.toString());

        window.open(intentUrl.toString(), "_blank", "noopener,noreferrer");
    }

    showTop() {
        if (this.isDrawing) return;

        this.disableResultReveal();
        this.resultScrollHint?.classList.remove("-show", "-shown");
        this.mount.replaceChildren();
        this.resultContent.removeAttribute("data-result-index");
        this.currentTitle = null;
        this.currentShareId = null;

        // PC外側の案内は初回だけ演出し、TOP復帰時は表示済みのままにする。
        document.body.classList.add("-yomikuji-top-replay");
        document.body.classList.remove("-yomikuji-drawing", "-yomikuji-aura-active", "-yomikuji-result", "-load");

        // -loadなしの初期状態を確定させてから再付与し、初回と同じ演出を再発火させる。
        this.top.getBoundingClientRect();
        document.body.classList.add("-load");

        this.drawButton.focus({ preventScroll: true });
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    async draw() {
        if (this.isDrawing || this.isPreparing) return;

        if (!this.results) {
            // 先読みが間に合っていないときだけ待つ。待機中の再押下は多重抽選になるため止める。
            this.isPreparing = true;
            this.drawButton.disabled = true;

            const loaded = await this.loadResults();

            this.isPreparing = false;
            this.drawButton.disabled = false;
            if (!loaded?.length) return;
        }

        const result = selectRandomResult(this.results, this.currentId);
        if (!result) return;

        this.showResult(result);
    }

    buildResultFragment(result) {
        const fragment = this.template.content.cloneNode(true);

        const setImage = (selector, src, alt = "") => {
            const image = fragment.querySelector(selector);
            if (!image || !src) return;

            image.src = src;
            image.alt = alt;
        };

        const setText = (selector, text) => {
            const element = fragment.querySelector(selector);
            if (element) element.textContent = text ?? "";
        };

        const isSceneTall = Boolean(result.sceneTall);
        fragment.querySelector(".js-yomikuji-fortune")?.classList.toggle("-tall", isSceneTall);
        fragment.querySelector(".js-yomikuji-scene")?.classList.toggle("-tall", isSceneTall);

        fragment.querySelectorAll(".js-yomikuji-side-image").forEach((image) => {
            image.src = result.sideImage;
        });

        // 下段の文字は個別指定がある結果だけ差し替え、未設定なら上段を左右入れ替えて表示する。
        setText(".js-yomikuji-corner-top-left", result.cornerLeft);
        setText(".js-yomikuji-corner-top-right", result.cornerRight);
        setText(".js-yomikuji-corner-bottom-left", result.cornerBottomLeft ?? result.cornerRight);
        setText(".js-yomikuji-corner-bottom-right", result.cornerBottomRight ?? result.cornerLeft);

        setImage(".js-yomikuji-title-image", result.titleImage, result.title);
        setImage(".js-yomikuji-scene-image", result.image);
        setImage(".js-yomikuji-message", result.messageImage, result.messageAlt);

        // 読みは ribbonImage がある結果だけ画像で出し、未設定の結果はテキスト側だけを残す。
        const ribbonImage = fragment.querySelector(".js-yomikuji-ribbon-image");
        const ribbonFallback = fragment.querySelector(".js-yomikuji-ribbon-fallback");

        if (result.ribbonImage) {
            setImage(".js-yomikuji-ribbon-image", result.ribbonImage, result.reading);
            ribbonFallback?.remove();
        } else {
            setText(".js-yomikuji-ribbon-fallback", result.reading);
            ribbonImage?.remove();
        }

        return fragment;
    }

    showResult(result, { skipAnimations = false } = {}) {
        if (this.isDrawing || !result) return;

        this.isDrawing = true;
        this.result.setAttribute("aria-busy", "true");
        this.drawButton.disabled = true;

        this.mount.replaceChildren(this.buildResultFragment(result));
        // 結果の増減や並び替えがあっても、結果別のタイトル幅と全高をIDへ正しく対応させる。
        this.resultContent.dataset.resultIndex = result.id;
        this.currentId = result.id;
        this.currentTitle = result.title;
        this.currentShareId = result.debugToken;

        this.disableResultReveal();
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });

        if (skipAnimations) {
            this.revealResult({ skipAnimations });
            return;
        }

        // 結果DOMは先に準備し、抽選待機中に画像を読み込んで表示開始時の欠けを避ける。
        document.body.classList.add("-yomikuji-drawing", "-yomikuji-aura-active");

        window.setTimeout(() => this.revealResult(), DRAWING_DURATION);
    }

    revealResult({ skipAnimations = false } = {}) {
        document.body.classList.remove("-yomikuji-drawing");
        document.body.classList.add("-yomikuji-result");
        this.setSideAnimationDuration();

        const heading = this.mount.querySelector(".js-yomikuji-result-heading");
        if (heading) heading.focus({ preventScroll: true });

        requestAnimationFrame(() => {
            this.enableResultReveal();
            this.updateResultScrollHint();

            if (skipAnimations) {
                this.finishDebugAnimations();
                this.resultScrollHint?.classList.add("-shown");
            }

            this.result.removeAttribute("aria-busy");
            this.drawButton.disabled = false;
            this.isDrawing = false;
        });
    }
}

document.addEventListener("DOMContentLoaded", () => {
    new Yomikuji();
});
